import styled from '@emotion/styled'
import { BidderAdapter } from '@pubstack/common/src/adm/bidder-adapter'
import { PbjsBidderConfigs, Schain, SchainNode } from '@pubstack/common/src/prebidConfig'
import { AdmSiteConfig } from '@pubstack/common/src/siteConfig'
import { FunctionComponent, useEffect, useState } from 'react'
import { SubmitHandler, useFieldArray, useForm, useWatch } from 'react-hook-form'
import { Colors } from '~/assets/style/colors'
import { useScopePermissions } from '~/auth/useScopePermissions'
import { Fonts } from '~/assets/style/fonts'
import Button from '~/components/Button'
import { ChipListInput } from '~/components/ChipListInput'
import { Icon } from '~/components/Icon'
import { BaseInputElement, Input } from '~/components/Input'
import { Link } from '~/components/Link'
import { Message } from '~/components/Message'
import { PopoverTriggerWrapper } from '~/components/Popover'
import { Toggle } from '~/components/Toggle'
import { Tooltip } from '~/components/Tooltip'
import { WidgetMessage } from '~/components/WidgetMessage'
import { WithClassName } from '~/types/utils'

const SiteConfigForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

/**
 * Turns the settings above the actions read-only in one place.
 *
 * `display: contents` because a fieldset is a box and this form is a flex column: without it the whole
 * body would collapse into a single flex child and lose the 24px rhythm between sections. The element
 * carries no border or padding of its own for the same reason — it exists only for the `disabled` it
 * propagates to every control inside.
 */
const ReadOnlyFieldset = styled.fieldset`
  display: contents;
  border: none;
  margin: 0;
  padding: 0;
`

const KVMSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 760px;
`

const Title = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 4px;
  padding-top: 20px;
  ${Fonts.H2};
  font-weight: bold;
  color: ${Colors.Jet};
`

const Help = styled.div`
  ${Fonts.P2};
  ${Fonts.colors.SlateGrey};
  margin-left: 50px;
  margin-top: -4px;
`

const FormToggle = styled.div`
  display: inline-flex;
  gap: 8px;
  flex-wrap: wrap;
  ${Tooltip} {
    display: inline-flex;
  };

  & ${Help} {
    flex-basis: 100%;
  }
`
const Actions = styled.div`
  display: flex;
  gap: 20px;
  align-items: center;
  justify-content: flex-end;
`
const LabelWithTooltip = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

const SchainItem = styled.div`
  display: inline-flex;
  gap: 25px;
  align-items: center;
  ${BaseInputElement} {
    width: 478px;
  };

  input:placeholder-shown {
    font-style: italic;
  }

  & ${Button} {
    align-self: center;
  }
`

const SchainBloc = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 39px;
  align-items: flex-start;
`

const SchainBidders = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 8px;
  ${PopoverTriggerWrapper} {
    flex-grow: 1;
  }
`

const SpecificSchainBloc = styled.div`
  ${BaseInputElement} {
    padding-bottom: 16px;
  }
`

const SchainActions = styled.div`
  display: inline-flex;
  gap: 16px;
`

const AuctionDelayBloc = styled.div`
  display: inline-flex;
  flex-direction: column;
  gap: 39px;
  align-items: flex-start;
`

const DEFAULT_SCHAIN_NODE: SchainNode = {
  asi: '',
  sid: '',
  hp: 1,
}
const DEFAULT_SCHAIN: Schain = {
  validation: 'strict',
  config: {
    ver: '1.0',
    complete: 1,
    nodes: [DEFAULT_SCHAIN_NODE],
  },
}
const DEFAULT_SPECIFIC_SCHAIN: PbjsBidderConfigs[] = [
  {
    bidders: [],
    config: {
      schain: DEFAULT_SCHAIN,
    },
  },
]

type PureAdmSiteConfigFormProps = WithClassName & {
  siteConfig: AdmSiteConfig
  // Awaited: Validate spins until the save ends.
  onCreateSiteConfig: (siteConfig: AdmSiteConfig) => void | Promise<void>
  bidderAdapters: BidderAdapter[]
  onPrebidLinkClick?: () => void
}

type SiteConfigFormValues = { defaultSiteConfig: AdmSiteConfig; isSchainEnabled: boolean; isSpecificSchainEnabled: boolean; hasInvalidSchain: boolean }

const preparePrebidSiteConfig = (siteConfig: AdmSiteConfig): AdmSiteConfig => {
  // migration data: ensure allowLocalStorage and sendKVTargetings are always present
  return {
    ...siteConfig,
    pbjsConfig: {
      ...siteConfig?.pbjsConfig,
      // the default behavior of kleanads.js is to allow local storage
      allowLocalStorage: siteConfig?.pbjsConfig?.allowLocalStorage ?? true,
      targetingBehaviors: {
        ...siteConfig?.pbjsConfig?.targetingBehaviors,
        sendKVTargetings: !!siteConfig?.pbjsConfig?.targetingBehaviors?.sendKVTargetings,
        biddersBlackList: siteConfig?.pbjsConfig?.targetingBehaviors?.biddersBlackList || [],
      },
      schain: siteConfig?.pbjsConfig?.schain ?? DEFAULT_SCHAIN,
      realTimeData: {
        ...siteConfig?.pbjsConfig?.realTimeData,
        auctionDelay: siteConfig?.pbjsConfig?.realTimeData?.auctionDelay ?? 0,
        dataProviders: siteConfig?.pbjsConfig?.realTimeData?.dataProviders,
      },
      bidderConfigs: siteConfig?.pbjsConfig?.bidderConfigs ?? DEFAULT_SPECIFIC_SCHAIN,
    },
  }
}

const toFormValues = (siteConfig: AdmSiteConfig): SiteConfigFormValues => ({
  defaultSiteConfig: preparePrebidSiteConfig(siteConfig),
  // For a media without its own, `schain` holds the scope's: the settings read fills it in.
  isSchainEnabled: !siteConfig?.isSchainDisabled && !!siteConfig?.pbjsConfig?.schain,
  isSpecificSchainEnabled: !!siteConfig?.pbjsConfig?.bidderConfigs,
  hasInvalidSchain: !!(siteConfig?.pbjsConfig?.bidderConfigs as PbjsBidderConfigs[])?.some((bidderConfig) => bidderConfig.config.schain && Object.keys(bidderConfig.config).length > 1),
})

const UnstyledPureAdmPrebidSiteConfigForm: FunctionComponent<PureAdmSiteConfigFormProps> = ({ onCreateSiteConfig, siteConfig: originalSiteConfig, bidderAdapters, onPrebidLinkClick }) => {
  /* The wrapper's settings are what every auction on this media runs under, so a `user` reads them and an
     owner changes them. */
  const { canEdit, disabledReason } = useScopePermissions()
  // migration data: ensure allowLocalStorage and sendKVTargetings are always present
  const {
    handleSubmit,
    formState: { isDirty, errors },
    setValue,
    getValues,
    reset,
    control,
  } = useForm<SiteConfigFormValues>({
    defaultValues: toFormValues(originalSiteConfig),
    mode: 'onChange',
  })

  const { defaultSiteConfig, isSchainEnabled, isSpecificSchainEnabled, hasInvalidSchain } = useWatch({ control })
  const { fields, append, remove } = useFieldArray({ control, name: 'defaultSiteConfig.pbjsConfig.schain.config.nodes' })
  const {
    fields: specificConfig,
    append: specificConfigAppend,
    remove: specificConfigRemove,
    update: specificConfigUpdate,
  } = useFieldArray({ control, name: 'defaultSiteConfig.pbjsConfig.bidderConfigs' })
  const [isFormSubmit, setFormSubmit] = useState<boolean>(false)

  const onSiteConfigSubmit: SubmitHandler<SiteConfigFormValues> = async (formValue) => {
    setFormSubmit(true)
    // A media with nothing to turn off stays unflagged, so it picks up its scope's supply chain once there is one.
    const hadSchain = !!originalSiteConfig?.isSchainDisabled || !!originalSiteConfig?.pbjsConfig?.schain
    try {
      await onCreateSiteConfig({
        ...formValue.defaultSiteConfig,
        isSchainDisabled: !formValue.isSchainEnabled && hadSchain ? true : undefined,
        pbjsConfig: {
          ...formValue.defaultSiteConfig.pbjsConfig,
          schain: formValue.isSchainEnabled ? formValue.defaultSiteConfig.pbjsConfig?.schain : undefined,
          bidderConfigs: formValue.isSpecificSchainEnabled ? formValue.defaultSiteConfig.pbjsConfig?.bidderConfigs : undefined,
        },
      })
    } catch (_err) {
      // The caller shows the error. The form only has to let the author try again.
    }
    setFormSubmit(false)
  }

  const addOrRemoveNode = (index: number, addNode: boolean, nodeIndex?: number) => {
    const currentConfig = getValues(`defaultSiteConfig.pbjsConfig.bidderConfigs.${index}`)
    if (!currentConfig) return
    const updatedNodes = currentConfig.config.schain?.config.nodes || []
    addNode ? updatedNodes.push(DEFAULT_SCHAIN_NODE) : nodeIndex && updatedNodes.splice(nodeIndex, 1)
    const bidderConfig = structuredClone(currentConfig)
    if (bidderConfig.config.schain) {
      bidderConfig.config.schain.config.nodes = updatedNodes
    }
    specificConfigUpdate(index, bidderConfig)
    setValue(`defaultSiteConfig.pbjsConfig.bidderConfigs.${index}`, bidderConfig, { shouldDirty: true })
  }

  const restore = () => {
    reset(toFormValues(originalSiteConfig))
  }

  // Not a bare `reset()`: that restores the values the form mounted with, not the config the save returned.
  useEffect(() => {
    reset(toFormValues(originalSiteConfig))
  }, [originalSiteConfig])

  return (
    <SiteConfigForm id={'siteConfigForm'} onSubmit={handleSubmit(onSiteConfigSubmit)}>
      {/* The fieldset covers the native controls — the asi/sid inputs, the auction delay, the buttons,
          all of which take `disabled` from an ancestor fieldset and style off `:disabled`. It does NOT
          cover `Toggle` or `ChipListInput`: both hang their click handler on a wrapper div guarded by
          their own `disabled` prop, so the fieldset reaches the hidden input inside them and nothing
          else — the switch stays clickable and stays looking enabled. Those two are passed `!canEdit`
          one by one below, and a new one added here must be too. `contents` keeps the fieldset out of
          the layout, so the form's own flex column still applies. */}
      <ReadOnlyFieldset disabled={!canEdit}>
        <Title>Local storage settings</Title>
        <FormToggle>
          <Toggle
            id={'pbjsConfig.allowLocalStorage'}
            disabled={!canEdit}
            value={defaultSiteConfig?.pbjsConfig?.allowLocalStorage}
            onClick={() => {
              setValue('defaultSiteConfig.pbjsConfig.allowLocalStorage', !defaultSiteConfig?.pbjsConfig?.allowLocalStorage, { shouldDirty: true })
            }}
            trackingLabel={defaultSiteConfig?.pbjsConfig?.allowLocalStorage ? 'adm-sites-stacks-prebid-setting-local-storage-unset' : 'adm-sites-stacks-prebid-setting-local-storage-set'}
          />
          <span>Allow use of cookies and/or local storage</span>
        </FormToggle>

        <Title>Key value targeting</Title>
        <FormToggle>
          <Toggle
            id={'pbjsConfig.targetingBehaviors.sendKVTargetings'}
            disabled={!canEdit}
            value={defaultSiteConfig?.pbjsConfig?.targetingBehaviors?.sendKVTargetings}
            onClick={() => {
              setValue('defaultSiteConfig.pbjsConfig.targetingBehaviors.sendKVTargetings', !defaultSiteConfig?.pbjsConfig?.targetingBehaviors?.sendKVTargetings, { shouldDirty: true })
              setValue('defaultSiteConfig.pbjsConfig.targetingBehaviors.biddersBlackList', [])
            }}
            trackingLabel={
              defaultSiteConfig?.pbjsConfig?.targetingBehaviors?.sendKVTargetings ? 'adm-prebid-settings-sending-key-value-targeting-unset' : 'adm-prebid-settings-sending-key-value-targeting-set'
            }
          />
          <span>Send Key Value targeting to all compatible bidders</span>
        </FormToggle>

        <KVMSection>
          <span>Don't send key value targeting to the following bidders:</span>
          <ChipListInput
            control={control}
            name={'defaultSiteConfig.pbjsConfig.targetingBehaviors.biddersBlackList'}
            label={'Excluded bidders'}
            disabled={!canEdit || !defaultSiteConfig?.pbjsConfig?.targetingBehaviors?.sendKVTargetings}
            isSpaceAllowed
            selectableOptions={bidderAdapters.map((bidderAdapter) => {
              return { label: bidderAdapter.displayName, value: bidderAdapter.codeOrAlias, trackingLabel: 'adm-prebid-settings-key-value-targeting-set' }
            })}
          />
        </KVMSection>

        <Title>SupplyChain (schain) settings</Title>
        <FormToggle>
          <Toggle
            id={'isSchainEnabled'}
            disabled={!canEdit}
            value={!!isSchainEnabled}
            onClick={() => {
              setValue('isSchainEnabled', !isSchainEnabled, { shouldDirty: true })
            }}
            trackingLabel={isSchainEnabled ? 'adm-prebid-settings-supply-chain-unset' : 'adm-prebid-settings-sending-supply-chain-set'}
          />
          <LabelWithTooltip>
            <span>Default SupplyChain configuration</span>
            <Tooltip
              title={
                'The SupplyChain Object enables buyers and intermediaries to see all parties who are selling or reselling ad inventory. The object works together with ads.txt / app-ads.txt and sellers.json to provide transparency into the ads ecosystem.'
              }
            >
              <Icon width={'20px'} name={'info'} />
            </Tooltip>
          </LabelWithTooltip>
        </FormToggle>
        {isSchainEnabled && (
          <SchainBloc>
            {fields.map((field, index) => (
              <SchainItem key={field.id}>
                <Input
                  label={'asi (advertising system identifier)'}
                  helper={'Domain name of the advertiser system'}
                  control={control}
                  name={`defaultSiteConfig.pbjsConfig.schain.config.nodes.${index}.asi`}
                  rules={{
                    required: 'Required',
                  }}
                  error={errors.defaultSiteConfig?.pbjsConfig && defaultSiteConfig?.pbjsConfig?.schain?.config?.nodes?.[index]?.asi === '' ? 'Required' : ''}
                />
                <Input
                  label={'sid (seller ID)'}
                  helper={'Used to identify the seller of the inventory'}
                  control={control}
                  name={`defaultSiteConfig.pbjsConfig.schain.config.nodes.${index}.sid`}
                  rules={{
                    required: 'Required',
                  }}
                  error={errors.defaultSiteConfig?.pbjsConfig && defaultSiteConfig?.pbjsConfig?.schain?.config?.nodes?.[index]?.sid === '' ? 'Required' : ''}
                />
                {index > 0 && (
                  <Button
                    onClick={() => {
                      remove(index)
                    }}
                    variant={'tertiary'}
                    iconName={'delete'}
                    trackingLabel={'adm-prebid-settings-supply-chain-delete'}
                  />
                )}
              </SchainItem>
            ))}
            <Button variant={'secondary'} iconName={'add'} onClick={() => append(DEFAULT_SCHAIN_NODE, { shouldFocus: false })} trackingLabel={'adm-prebid-settings-supply-chain-new'}>
              Add
            </Button>
          </SchainBloc>
        )}
        <FormToggle>
          <Toggle
            id={'isSpecificSchainEnabled'}
            value={!hasInvalidSchain && !!isSpecificSchainEnabled}
            disabled={hasInvalidSchain || !canEdit}
            onClick={() => {
              setValue('isSpecificSchainEnabled', !isSpecificSchainEnabled, { shouldDirty: true })
              specificConfig.length === 0 && setValue('defaultSiteConfig.pbjsConfig.bidderConfigs', DEFAULT_SPECIFIC_SCHAIN)
            }}
            trackingLabel={isSpecificSchainEnabled ? 'adm-prebid-settings-supply-chain-specific-unset' : 'adm-prebid-settings-sending-supply-chain-specific-set'}
          />
          <LabelWithTooltip>
            <span>Apply SupplyChain settings for specific bidders</span>
          </LabelWithTooltip>
        </FormToggle>
        {hasInvalidSchain && (
          <Message
            status={'warning'}
            title={'Warning'}
            description={'A non-schain config exists in prebid configuration backend. Please contact support team to add bidder-specific supply chain config'}
          />
        )}

        {!hasInvalidSchain && isSpecificSchainEnabled && (
          <>
            <WidgetMessage
              icon={'warning'}
              message={
                <>
                  For the selected bidders, these nodes are added after the nodes of your default SupplyChain configuration
                  {onPrebidLinkClick && (
                    <>
                      {' '}
                      and will be applied on <Link onClick={onPrebidLinkClick} label={'prebid integration wrapper'} trackingLabel={'adm-prebid-settings-from-schain-link-view'} />
                    </>
                  )}
                </>
              }
            />
            {specificConfig.map((biddersConfig, index) => {
              return (
                <SpecificSchainBloc key={biddersConfig.id}>
                  <SchainBidders>
                    <ChipListInput
                      control={control}
                      name={`defaultSiteConfig.pbjsConfig.bidderConfigs.${index}.bidders`}
                      label={'Select your bidders'}
                      disabled={!canEdit}
                      isSpaceAllowed
                      selectableOptions={bidderAdapters.map((bidderAdapter) => {
                        return {
                          label: `${bidderAdapter.codeOrAlias} (${bidderAdapter.displayName})`,
                          value: bidderAdapter.codeOrAlias,
                          trackingLabel: 'adm-prebid-settings-schain-specific-bidders-set',
                        }
                      })}
                    />
                    <Button
                      variant={'secondary'}
                      iconName={'delete'}
                      onClick={() => {
                        specificConfigRemove(index)
                        specificConfig.length === 1 && setValue('isSpecificSchainEnabled', !isSpecificSchainEnabled, { shouldDirty: true })
                      }}
                      trackingLabel={'adm-prebid-settings-supply-chain-delete'}
                    >
                      Delete setting
                    </Button>
                  </SchainBidders>
                  <SchainBloc>
                    {(defaultSiteConfig as AdmSiteConfig)?.pbjsConfig?.bidderConfigs?.[index]?.config?.schain?.config?.nodes?.map((node: SchainNode, nodeIndex: number) => (
                      <SchainItem key={nodeIndex}>
                        <Input
                          label={'asi (advertising system identifier)'}
                          helper={'Domain name of the advertiser system'}
                          control={control}
                          name={`defaultSiteConfig.pbjsConfig.bidderConfigs.${index}.config.schain.config.nodes.${nodeIndex}.asi`}
                          rules={{
                            required: 'Required',
                          }}
                          error={errors.defaultSiteConfig?.pbjsConfig && node?.asi === '' ? 'Required' : ''}
                        />
                        <Input
                          label={'sid (seller ID)'}
                          helper={'Used to identify the seller of the inventory'}
                          control={control}
                          name={`defaultSiteConfig.pbjsConfig.bidderConfigs.${index}.config.schain.config.nodes.${nodeIndex}.sid`}
                          rules={{
                            required: 'Required',
                          }}
                          error={errors.defaultSiteConfig?.pbjsConfig && node?.sid === '' ? 'Required' : ''}
                        />
                        {nodeIndex > 0 && (
                          <Button
                            onClick={() => {
                              addOrRemoveNode(index, false, nodeIndex)
                            }}
                            variant={'tertiary'}
                            iconName={'delete'}
                            trackingLabel={'adm-prebid-settings-specific-supply-chain-delete'}
                          />
                        )}
                      </SchainItem>
                    ))}
                    <SchainActions>
                      {index === specificConfig.length - 1 && (
                        <Button
                          variant={'primary'}
                          iconName={'add'}
                          onClick={() => specificConfigAppend(DEFAULT_SPECIFIC_SCHAIN, { shouldFocus: false })}
                          trackingLabel={'adm-prebid-settings-supply-chain-new'}
                        >
                          Add new setting
                        </Button>
                      )}
                      <Button variant={'secondary'} iconName={'add'} onClick={() => addOrRemoveNode(index, true)} trackingLabel={'adm-prebid-settings-supply-chain-node-new'}>
                        Add node
                      </Button>
                    </SchainActions>
                  </SchainBloc>
                </SpecificSchainBloc>
              )
            })}
          </>
        )}

        <Title>Transaction identifiers</Title>
        <FormToggle>
          <Toggle
            id={'pbjsConfig.enableTIDs'}
            disabled={!canEdit}
            value={defaultSiteConfig?.pbjsConfig?.enableTIDs}
            onClick={() => {
              setValue('defaultSiteConfig.pbjsConfig.enableTIDs', !defaultSiteConfig?.pbjsConfig?.enableTIDs, { shouldDirty: true })
            }}
            trackingLabel={isSchainEnabled ? 'adm-prebid-settings-transaction-identifiers-unset' : 'adm-prebid-settings-sending-transaction-identifiers-set'}
          />
          <span>Use TIDs</span>
          <Help>Prebid generates unique IDs for auctions and ad units to help DSPs correlate requests.</Help>
        </FormToggle>

        <Title>
          Auction delay
          <Tooltip
            title={
              'Defines the maximum amount of time, in milliseconds, the header bidding auction will be delayed while waiting for a response from the RTD modules as a whole group. The default is 0 ms delay, which means that RTD modules need to obtain their data when the page initializes.'
            }
          >
            <Icon width={'20px'} name={'info'} />
          </Tooltip>
        </Title>
        <AuctionDelayBloc>
          <Input type={'number'} label={'Auction delay'} control={control} name={'defaultSiteConfig.pbjsConfig.realTimeData.auctionDelay'} min={0} step={1} iconRight={'millisecond'} />
        </AuctionDelayBloc>
      </ReadOnlyFieldset>
      <Actions>
        {isDirty && (
          <Button onClick={restore} iconName={'restore'} variant={'tertiary'} trackingLabel={'adm-prebid-settings-restore'}>
            Restore
          </Button>
        )}
        <Tooltip title={disabledReason} disabled={canEdit}>
          <Button
            disabled={!isDirty || isFormSubmit || !canEdit}
            variant={'primary'}
            type={'submit'}
            iconSpin={isFormSubmit}
            iconName={isFormSubmit ? 'pubstack' : undefined}
            trackingLabel={'adm-prebid-settings-save'}
          >
            Validate
          </Button>
        </Tooltip>
      </Actions>
    </SiteConfigForm>
  )
}

export const PureAdmPrebidSiteConfigForm = styled(UnstyledPureAdmPrebidSiteConfigForm)``
